import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import {
  ISyncTranslationsResult,
  ITranslationSyncLanguageDetail,
} from "../../../@types/admin/AdminMasterTranslations";
import { TableSorted } from "../../../components/TableSorted";
import { useSyncTranslations } from "../../../hooks/mutations/admin/masterTranslations";
import { useTranslationsSyncKeyDetail } from "../../../hooks/queries/admin/masterTranslations";
import { useToast } from "../../../hooks/useToast";
import { serverErrorMessage } from "../../../utils/HttpError";
import {
  FilterButton,
  ModalContainer,
  ModalFormActions,
  ModalTitle,
  StatusBadge,
  TableLoadingState,
  TableWrapper,
} from "../shared";
import { ModalBodyText } from "./AdminTranslations.styles";
import { syncStateBadge, syncStateLabel } from "./syncState";

interface ISyncKeyDetailModalProps {
  translationKey: string;
  usedLocation: string;
  environmentName: string;
  onClose: () => void;
}

/** "3 updated, 2 added" — what a sync call actually did, for the toast. */
export function describeSyncResult(result: ISyncTranslationsResult): string {
  const outcomes = result.results ?? [];
  const inserted = outcomes.filter((r) => r.outcome === "Inserted").length;
  const updated = outcomes.filter((r) => r.outcome === "Updated").length;
  const unchanged = outcomes.filter((r) => r.outcome === "Unchanged").length;

  const parts: string[] = [];
  if (inserted > 0) parts.push(`${inserted} added`);
  if (updated > 0) parts.push(`${updated} updated`);
  if (parts.length === 0) {
    parts.push(unchanged > 0 ? "already in sync" : "nothing to sync");
  }
  if ((result.addedLanguages ?? []).length > 0) {
    parts.push(`languages added: ${result.addedLanguages.join(", ")}`);
  }
  return parts.join(", ");
}

/**
 * Master vs this environment for one key, per language — the portal's version
 * of Retool migrate_GlobalTranslations' fullscreen diff modal (EN-5178),
 * reduced to the ONE environment this portal can write to.
 */
export function SyncKeyDetailModal({
  translationKey,
  usedLocation,
  environmentName,
  onClose,
}: ISyncKeyDetailModalProps) {
  const { showToast } = useToast();
  const { detail, isLoading, isError } = useTranslationsSyncKeyDetail(
    translationKey,
    usedLocation
  );
  const { syncTranslations, isSyncing } = useSyncTranslations();

  const languages = detail?.languages ?? [];
  const pendingCount = languages.filter((l) => l.state !== "InSync").length;

  const handleSync = async (languageCodes?: string[]) => {
    try {
      const result = await syncTranslations({
        items: [{ key: translationKey, usedLocation, languageCodes }],
      });
      showToast(
        `Synced ${translationKey}: ${describeSyncResult(result)}.`,
        "success"
      );
    } catch (error) {
      showToast(
        serverErrorMessage(error) ?? "The sync failed. Nothing was changed.",
        "warning"
      );
    }
  };

  const columns: ColumnDef<ITranslationSyncLanguageDetail, string>[] = useMemo(
    () => [
      {
        accessorKey: "languageCode",
        header: "Language",
        enableSorting: true,
      },
      {
        accessorKey: "masterValue",
        header: "Master value",
        enableSorting: false,
      },
      {
        id: "localValue",
        header: `Value in ${environmentName}`,
        // A missing local value renders as the table's own "-" empty cell.
        accessorFn: (l) => l.localValue ?? "",
        enableSorting: false,
      },
      {
        id: "state",
        header: "Status",
        accessorFn: (l) => l.state,
        cell: ({ row }) => (
          <StatusBadge status={syncStateBadge(row.original.state)}>
            {syncStateLabel(row.original.state)}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) =>
          row.original.state === "InSync" ? null : (
            <FilterButton
              variant="secondary"
              disabled={isSyncing}
              onClick={() => handleSync([row.original.languageCode])}
            >
              Sync
            </FilterButton>
          ),
        enableSorting: false,
      },
    ],
    // (react-hooks/exhaustive-deps is not configured here; the omitted deps
    // are the modal openers, which are stable for the column cells.)
    [environmentName, isSyncing]
  );

  return (
    <ModalContainer>
      <ModalTitle css={{ overflowWrap: "anywhere" }}>
        {translationKey}
      </ModalTitle>

      <ModalBodyText>
        Master store vs the <strong>{environmentName}</strong> environment (used
        location: <strong>{usedLocation}</strong>).
      </ModalBodyText>

      {isError && (
        <ModalBodyText>
          Failed to load this key&apos;s comparison. Close and try again.
        </ModalBodyText>
      )}

      <TableWrapper refreshKeys={[["admin-translations-sync-key-detail"]]}>
        <TableSorted
          columns={columns}
          data={languages}
          emptyStateText={isLoading ? <TableLoadingState /> : "No languages"}
        />
      </TableWrapper>

      <ModalFormActions>
        <FilterButton
          variant="secondary"
          disabled={isSyncing}
          onClick={onClose}
        >
          Close
        </FilterButton>
        <FilterButton
          variant="primary"
          disabled={isSyncing || pendingCount === 0}
          onClick={() => handleSync()}
        >
          {isSyncing
            ? "Syncing..."
            : `Sync all languages (${pendingCount} pending)`}
        </FilterButton>
      </ModalFormActions>
    </ModalContainer>
  );
}
